import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Globe2,
  ShieldCheck,
  Truck,
  ChevronRight,
  Sparkles,
  Clock3,
  BadgeCheck,
  MapPin,
  FileText,
  Building2,
  Factory,
  ScanSearch,
  ClipboardList,
} from "lucide-react";
import RFQDialog from "@/components/RFQDialog";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import useCustomerAppSession from "@/hooks/useCustomerAppSession";
import {
  usePublicCertificates,
  usePublicSettings,
} from "@/hooks/usePublicSiteData";
import { toast } from "sonner";

function scrollToSection(sectionId) {
  if (typeof document === "undefined") return;
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

export default function Home() {
  const [rfqOpen, setRfqOpen] = useState(false);
  const [portalAccessId, setPortalAccessId] = useState("");
  const [portalAccessCode, setPortalAccessCode] = useState("");
  const [portalError, setPortalError] = useState("");
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { language, isZh, isBn } = useSiteLanguage();
  const customerSession = useCustomerAppSession();

  const { data: settings } = usePublicSettings();
  const { data: certificates = [] } = usePublicCertificates();

  useEffect(() => {
    if (videoRef.current) videoRef.current.play().catch(() => {});
  }, [settings?.hero_video_url]);

  const heroVideo = settings?.hero_video_url || "/rs-hero.mp4";
  const complianceCount = certificates.length || 0;

  const copy = isZh
    ? {
        heroBadge: "受到 30+ 国家进口商关注",
        heroHeadline: "连接全球市场，交付可信贸易。",
        heroSubheadline:
          "R.S. International 面向全球 B2B 买家提供进口、供应、市场协调与跨境贸易支持，重视结构化沟通、可追踪流程与长期合作。",
        heroPrimaryCta: "立即咨询",
        heroSecondaryCta: "浏览目录",
        heroTrust1: "ISO 与贸易合规导向",
        heroTrust2: "全球采购协作网络",
        heroTrust3: "端到端物流支持",
        divisionsBadge: "专业产品事业部",
        divisionsTitle: "让买家在首屏下方立即看见你真正经营的领域。",
        divisionsBody:
          "大型 B2B 买家首先想确认你是否具备明确的产品分工、技术理解与批量执行能力。下面这两个事业部卡片会直接把买家带到更具体的规格与订购信息。",
        divisionPrimaryCta: "查看 B2B 规格",
        divisionSecondaryCta: "批量订购流程",
        divisionDetailCta: "获取完整规格包",
        trustBadge: "产品与信任结构",
        trustPanelTitle: "进口商判断一家供应公司，看的不是口号，而是结构、证据与执行路径。",
        trustPanelBody:
          "这一部分不仅展示经营范围，也展示让谨慎买家愿意继续浏览、继续沟通、继续评估的关键信号。",
        trustCenterCta: "查看信任中心",
        companyStoryCta: "阅读企业介绍",
        proofEstablished: "企业成立",
        proofCompliance: "公开合规资料",
        proofResponse: "贸易响应",
        proofBase: "公司所在地",
        proofEstablishedValue: settings?.company_since ? `自 ${settings.company_since} 年起` : "长期贸易积累",
        proofComplianceValue: `${complianceCount} 份公开资料`,
        proofResponseValue: "24 个工作小时内回复询盘",
        proofBaseValue: settings?.company_location || settings?.address || "孟加拉国 巴里萨尔",
        confidenceTitle1: "推荐前先做核验",
        confidenceBody1: "围绕供应方文件、可执行性、质量与商业匹配度先做审查，再进入正式采购讨论。",
        confidenceTitle2: "单一贸易团队负责到底",
        confidenceBody2: "买家面对的是一个可追责的业务窗口，而不是分散且难以验证的中间层。",
        confidenceTitle3: "从报价跟进到交付协作",
        confidenceBody3: "从询价、谈判到物流协调和客户沟通，过程更加连贯、清晰、可追踪。",
        seriousImporterMessage: "严肃买家看到清晰企业身份、合规可见性与流程纪律后，更愿意继续停留在网站并相信背后的公司。",
        fullCatalogCta: "查看完整目录",
        globalBadge: "Global Reach & Operations",
        globalTitle: "展示你如何跨境运作，往往比单纯说“我们做国际贸易”更能建立信任。",
        globalBody:
          "这一板块以信息图方式呈现总部、贸易走廊与伙伴节点，帮助买家快速理解你如何连接孟加拉国、中国、格鲁吉亚和欧洲市场。",
        governanceBadge: "企业治理预览",
        governanceTitle: "在签订大额合同之前，B2B 买家会先看企业是谁在负责。",
        governanceBody:
          "把使命、成立历史、领导结构和企业路径放在可快速浏览的位置，会显著提高网站的专业感与可托付感。",
        governanceMissionLabel: "企业使命",
        governanceMission:
          "以可信沟通、结构化贸易执行与长期合作关系为基础，为跨境买家提供更安全、更清晰的采购协作体验。",
        governanceHistoryLabel: "成立历史",
        governanceHistory:
          settings?.company_since
            ? `R.S. International 自 ${settings.company_since} 年起建立贸易基础，并持续由管理层推动国际业务扩展。`
            : "R.S. International 拥有长期贸易基础，并持续由管理层推动国际业务扩展。",
        governanceLeadershipLabel: "领导与企业资料",
        governanceLeadership:
          "买家可继续查看企业介绍、创始人传承、现任领导层与公开信任资料，以建立更完整的尽调印象。",
        governancePrimaryCta: "查看企业介绍",
        governanceSecondaryCta: "查看管理层页面",
        featuredBadge: "精选展示与买家停留",
        featuredTitle: "让买家留下来的，不只是页面设计，而是可判断、可核验、可继续推进的内容。",
        featuredSubtitle:
          "当潜在客户能在同一屏看到产品逻辑、公司可信度以及下一步行动入口时，网站会显得更稳健、更像一家真正可以合作的跨国 B2B 公司。",
        retentionTitle1: "跨境沟通要清楚",
        retentionBody1: "产品、交付、文件和采购条件说明越专业，海外买家越愿意继续停留并深入了解。",
        retentionCta1: "查看企业资料",
        retentionTitle2: "信任内容可被审阅",
        retentionBody2: "证书、公司历史和公开合规资料为谨慎买家提供了更多继续评估的理由。",
        retentionCta2: "打开信任中心",
        retentionTitle3: "有兴趣之后要能快速推进",
        retentionBody3: "可见的响应承诺与清晰的业务入口可以显著降低犹豫成本。",
        retentionCta3: "查看业务支持流程",
        featuredPanelLabel: "买家停留层",
        featuredPanelTitle: "严肃买家更愿意停留在一个能证明自己如何工作的公司网站上。",
        featuredPanelBody:
          "这一区域现在更像企业级 B2B 着陆页，而不是普通产品列表。它将目录、信任、下一步动作与企业信息串联在一起。",
        featuredPrimaryCta: "发起业务询盘",
        featuredSecondaryCta: "查看合规资料",
        realCatalogMode: "真实目录模式",
        realCatalogTitle: "网站现在只展示真实在线数据，不再展示示例库存。",
        realCatalogBody:
          "这意味着买家看到的是更诚实的采购业务形象，而不是演示型商城。即使当前精选产品为空，页面仍能通过结构化信任内容保持专业感。",
        nextActionLabel: "最佳下一步",
        nextActionTitle: "直接告诉贸易团队你的采购需求。",
        nextActionBody:
          "如果买家已有目标产品、目的国、MOQ 或文件要求，直接提交需求通常比单独浏览目录更高效。",
        nextActionPrimaryCta: "提交采购需求",
        nextActionSecondaryCta: "查看企业详情",
        whySectionMatters: "为什么这一部分很重要",
        whySectionMattersBody: "买家在网站上停留得越久，越能看到证据、流程与下一步路径，也就越容易相信你的公司。",
        complianceBadge: "合规与市场准入",
        complianceTitle: "医疗与工业贸易必须把合规语言放在显眼位置，但表达必须专业且真实。",
        complianceBody:
          "下面的横向标识条展示你面向国际买家时最关键的合规语言：医疗器械质量体系、欧洲市场合规、美国市场文件意识以及真实商会成员身份。",
        complianceNote:
          "注：实际文件与注册要求取决于产品、供应商、目的地和交易结构。该区域展示的是业务熟悉度与真实公开资质，不代表对所有产品作出统一认证声明。",
        portalBadge: "Client Supply Chain Portal",
        portalTitle: "把现有追踪技术重新包装为更专业的 B2B 客户供应链入口。",
        portalBody:
          "经验证的进口商可以通过专属访问凭证进入客户门户，跟踪大宗货运、查看发票协作状态、并重新订购常规库存。",
        portalMetric1: "已验证客户访问",
        portalMetric1Body: "基于 Access ID 与 Access Code 的客户级权限控制",
        portalMetric2: "批量货运追踪",
        portalMetric2Body: "围绕集装箱、参考号与 ETA 的专业追踪体验",
        portalMetric3: "复购与单据协作",
        portalMetric3Body: "让客户更方便地进行重复采购与业务跟进",
        portalFormTitle: "验证进口商登录",
        portalFormBody:
          "输入由后台管理面板生成的客户 Access ID 与 Access Code，即可从这里直接登录现代化客户供应链门户。",
        portalIdLabel: "Access ID",
        portalCodeLabel: "Access Code",
        portalOpenCta: "登录客户门户",
        portalTalkCta: "联系贸易团队",
        portalSigningIn: "正在登录...",
        portalMissingFields: "请输入 Access ID 和 Access Code。",
        portalLoginSuccess: "客户门户已成功登录。",
        portalSignedInLabel: "门户访问已启用",
        portalSignedInTitle: "你已从这里成功登录客户供应链门户。",
        portalSignedInBody:
          "现在可以直接进入客户专属工作区，只查看属于该客户的订单、货运与后续协作信息。",
        portalCustomerLabel: "客户",
        portalOrdersLabel: "订单",
        portalShipmentsLabel: "货运",
        portalOrderVisibility: "当前只显示该客户自己的订单数据。",
        portalShipmentVisibility: "当前只显示该客户自己的货运数据。",
        portalRecentOrders: "最近订单",
        portalRecentShipments: "最近货运",
        portalNoOrders: "该客户暂时还没有已关联订单。",
        portalNoShipments: "该客户暂时还没有已关联货运。",
        portalOpenWorkspace: "打开客户工作区",
        portalTrackLink: "查看货运追踪",
        portalSignOut: "退出登录",
        portalCard1: "追踪大宗货运",
        portalCard1Body: "查看运单里程碑、港口节点与 ETA 窗口。",
        portalCard2: "管理发票协作",
        portalCard2Body: "围绕参考号、文件与付款步骤保持有序沟通。",
        portalCard3: "安全复购库存",
        portalCard3Body: "已验证买家可以更快回到重复采购流程。",
        globalCard1Label: "总部与控制",
        globalCard1Title: "孟加拉国",
        globalCard1Body: "买家沟通、贸易团队协调与关系维护始终以孟加拉国总部为核心。",
        globalCard2Label: "贸易通道",
        globalCard2Title: "中国 · 格鲁吉亚 · 欧洲",
        globalCard2Body: "网站现在以清晰的国际业务路径来呈现你的采购与市场关系，而不是笼统的地理描述。",
        operationsHubLabel: "运营枢纽",
        operationsHubTitle: "孟加拉国总部",
        governancePreviewLabel: "尽调预览",
        governancePreviewTitle: "在买家发送第一份合同草案之前，企业结构就应当足够清晰可见。",
        governancePreviewBody:
          "这个治理预览模块旨在减少“这家公司背后到底是谁？”这种常常拖慢严肃 B2B 沟通的犹豫。",
        portalFallbackCustomer: "客户",
        portalFallbackAccess: "已发放访问权限",
        portalFallbackShipmentOpen: "货运处理中",
      }
    : isBn
      ? {
          heroBadge: "৩০টিরও বেশি দেশের আমদানিকারকদের আস্থা",
          heroHeadline: "বিশ্ববাজারকে যুক্ত করি, আস্থার সঙ্গে সরবরাহ করি।",
          heroSubheadline:
            "R.S. International বিশ্বজুড়ে B2B ক্রেতাদের জন্য আমদানি, সরবরাহ, বাজার-সমন্বয় এবং সীমান্তপার বাণিজ্য সহায়তা প্রদান করে; যেখানে গঠনমূলক যোগাযোগ, ট্র্যাকযোগ্য প্রক্রিয়া ও দীর্ঘমেয়াদি সম্পর্ককে অগ্রাধিকার দেওয়া হয়।",
          heroPrimaryCta: "এখনই জিজ্ঞাসা করুন",
          heroSecondaryCta: "ক্যাটালগ দেখুন",
          heroTrust1: "ISO ও ট্রেড কমপ্লায়েন্স-সমন্বিত",
          heroTrust2: "গ্লোবাল সোর্সিং সমন্বয় নেটওয়ার্ক",
          heroTrust3: "ডোর-টু-ডোর লজিস্টিক সহায়তা",
          divisionsBadge: "বিশেষায়িত পণ্য বিভাগ",
          divisionsTitle: "হিরো সেকশনের ঠিক নিচেই ক্রেতারা বুঝতে পারবে আপনার কোম্পানি আসলে কী কী পণ্য বাণিজ্য করে।",
          divisionsBody:
            "বড় B2B ক্রেতারা সামনে এগোনোর আগে খাতভিত্তিক দক্ষতা, প্রযুক্তিগত বোঝাপড়া এবং বাল্ক-অর্ডার প্রস্তুতি দেখতে চায়। এই বিভাগীয় কার্ডগুলো তাদেরকে স্পেসিফিকেশন-কেন্দ্রিক তথ্য ও বাল্ক অর্ডার প্রক্রিয়ায় নিয়ে যায়।",
          divisionPrimaryCta: "B2B স্পেসিফিকেশন দেখুন",
          divisionSecondaryCta: "বাল্ক অর্ডারের পথ",
          divisionDetailCta: "পূর্ণ স্পেসিফিকেশন প্যাক চাইুন",
          trustBadge: "পণ্য ও আস্থা স্থাপনা",
          trustPanelTitle: "আমদানিকারকরা সেই কোম্পানিকেই বিশ্বাস করে যারা কাঠামো, শৃঙ্খলা ও বাণিজ্যিক প্রস্তুতি প্রমাণ করতে পারে।",
          trustPanelBody:
            "এই অংশটি এখন শুধু ক্যাটাগরি দেখায় না। এটি সেই সংকেতগুলো দেখায় যা সতর্ক ক্রেতাকে প্রথম দেখাতেই সরে না গিয়ে আরও মূল্যায়নে এগিয়ে নিয়ে যায়।",
          trustCenterCta: "ট্রাস্ট সেন্টার দেখুন",
          companyStoryCta: "কোম্পানি প্রোফাইল পড়ুন",
          proofEstablished: "প্রতিষ্ঠিত",
          proofCompliance: "কমপ্লায়েন্স দৃশ্যমানতা",
          proofResponse: "বাণিজ্যিক সাড়া",
          proofBase: "কোম্পানির অবস্থান",
          proofEstablishedValue: settings?.company_since ? `${settings.company_since} সাল থেকে` : "দীর্ঘমেয়াদি বাণিজ্যিক ঐতিহ্য",
          proofComplianceValue: `${complianceCount}টি উন্মুক্ত আস্থার নথি`,
          proofResponseValue: "২৪ কর্মঘন্টার মধ্যে কোটেশনের উত্তর",
          proofBaseValue: settings?.company_location || settings?.address || "বরিশাল, বাংলাদেশ",
          confidenceTitle1: "সুপারিশের আগে যাচাই",
          confidenceBody1: "সরবরাহকারীর নথি, বাস্তবায়ন সক্ষমতা ও মান-উপযোগিতা যাচাই করে তবেই সোর্সিং আলোচনা প্রকৃত অর্ডার প্রক্রিয়ায় যায়।",
          confidenceTitle2: "একটি দায়বদ্ধ ট্রেড ডেস্ক",
          confidenceBody2: "ক্রেতারা বিচ্ছিন্ন মধ্যস্থতাকারীর বদলে একটি স্পষ্ট ব্যবসায়িক যোগাযোগ পয়েন্টের সঙ্গে কাজ করে।",
          confidenceTitle3: "ডেলিভারি পর্যন্ত ফলো-থ্রু",
          confidenceBody3: "কোটেশন, দরকষাকষি, চালান সমন্বয় ও ক্রেতা যোগাযোগ—সব কিছুই সুসংগঠিত ও ট্র্যাকযোগ্য থাকে।",
          seriousImporterMessage:
            "ক্রেতারা যখন পরিষ্কার কোম্পানি পরিচয়, দৃশ্যমান কমপ্লায়েন্স ও শৃঙ্খলাপূর্ণ প্রক্রিয়ার সংকেত দেখে, তখন তারা বেশি সময় থাকে এবং ব্যবসাটিকে দ্রুত বিশ্বাস করতে শুরু করে।",
          fullCatalogCta: "পূর্ণ ক্যাটালগ দেখুন",
          globalBadge: "গ্লোবাল উপস্থিতি ও কার্যক্রম",
          globalTitle: "ক্রেতারা যখন দেখতে পায় আপনি সীমান্ত পেরিয়ে কীভাবে কাজ করেন, তখন আন্তর্জাতিক আস্থা আরও দ্রুত গড়ে ওঠে।",
          globalBody:
            "এই ইনফোগ্রাফিক-ধাঁচের অংশটি বাংলাদেশে আপনার সদরদপ্তর এবং চীন, জর্জিয়া ও বৃহত্তর ইউরোপীয় বাজারজুড়ে আপনার বাণিজ্যিক সংযোগগুলো স্পষ্টভাবে তুলে ধরে।",
          governanceBadge: "কর্পোরেট গভর্ন্যান্স প্রিভিউ",
          governanceTitle: "বড় চুক্তিতে যাওয়ার আগে B2B ক্রেতারা জানতে চায় কোম্পানির পেছনে কারা আছেন।",
          governanceBody:
            "দৃশ্যমান মিশন স্টেটমেন্ট, প্রতিষ্ঠার ইতিহাস ও নেতৃত্বের পথ কোম্পানিকে আরও পরিপক্ব, দায়বদ্ধ ও উচ্চমূল্যের বাণিজ্যিক সম্পর্কের উপযোগী করে তোলে।",
          governanceMissionLabel: "মিশন স্টেটমেন্ট",
          governanceMission:
            "শৃঙ্খলাপূর্ণ যোগাযোগ, গঠিত বাস্তবায়ন ও দায়িত্বশীল সোর্সিং সহায়তার মাধ্যমে বিশ্বস্ত সীমান্তপার ক্রয়-সম্পর্ক গড়ে তোলা।",
          governanceHistoryLabel: "প্রতিষ্ঠার ইতিহাস",
          governanceHistory: settings?.company_since
            ? `R.S. International ${settings.company_since} সাল থেকে তার বাণিজ্যিক পরিচয় গড়ে তুলছে, যা বর্তমান নেতৃত্বের ধারাবাহিকতায় এগিয়ে চলছে।`
            : "R.S. International দীর্ঘদিনের বাণিজ্যিক ভিত্তির ওপর দাঁড়িয়ে বর্তমান নেতৃত্বের মাধ্যমে এগিয়ে চলছে।",
          governanceLeadershipLabel: "নেতৃত্ব ও কর্পোরেট প্রোফাইল",
          governanceLeadership:
            "সম্ভাব্য ক্রেতারা বড় লেনদেন শুরু করার আগে কোম্পানি প্রোফাইল, প্রতিষ্ঠাতার উত্তরাধিকার এবং নেতৃত্বের পেজ দেখে আরও পূর্ণাঙ্গ ডিউ-ডিলিজেন্স ধারণা নিতে পারে।",
          governancePrimaryCta: "কোম্পানি প্রোফাইল খুলুন",
          governanceSecondaryCta: "নেতৃত্বের সঙ্গে পরিচিত হোন",
          featuredBadge: "প্রধান ক্রেতা যাত্রাপথ",
          featuredTitle: "যে ওয়েবসাইটগুলো ক্রেতাকে দীর্ঘ সময় ধরে রাখে, সেগুলোই ঝুঁকি, প্রক্রিয়া ও পরবর্তী পদক্ষেপ স্পষ্ট করে।",
          featuredSubtitle:
            "যখন একটি সম্ভাব্য ক্রেতা একই জায়গায় সোর্সিং যুক্তি, কোম্পানির বিশ্বাসযোগ্যতা এবং সঠিক পরবর্তী পদক্ষেপ দেখতে পারে, তখন ওয়েবসাইটটি বাস্তব B2B ক্রয়ের জন্য অনেক বেশি নির্ভরযোগ্য মনে হয়।",
          retentionTitle1: "পরিষ্কার সীমান্তপার যোগাযোগ",
          retentionBody1: "পণ্য, ডেলিভারি ও ডকুমেন্টেশন যত নির্ভুলভাবে ব্যাখ্যা করা হয়, বিদেশি ক্রেতারা তত বেশি সময় ধরে সাইটে থাকে।",
          retentionCta1: "কোম্পানি প্রোফাইল দেখুন",
          retentionTitle2: "যাচাইযোগ্য আস্থার কনটেন্ট",
          retentionBody2: "সার্টিফিকেট, কোম্পানির ইতিহাস ও উন্মুক্ত কমপ্লায়েন্স তথ্য সতর্ক ক্রেতাকে আরও বেশি কারণ দেয় আপনাকে মূল্যায়ন চালিয়ে যেতে।",
          retentionCta2: "ট্রাস্ট সেন্টার খুলুন",
          retentionTitle3: "আগ্রহের পর দ্রুত অগ্রগতি",
          retentionBody3: "দৃশ্যমান সাড়া দেওয়ার প্রতিশ্রুতি ও পরিষ্কার ব্যবসায়িক পথ দ্বিধা কমায় এবং সিরিয়াস ক্রেতাকে এগোতে সাহায্য করে।",
          retentionCta3: "সাপোর্ট ফ্লো দেখুন",
          featuredPanelLabel: "ক্রেতা ধরে রাখার স্তর",
          featuredPanelTitle: "যে ব্যবসা নিজের কাজের পদ্ধতি প্রমাণ করতে পারে, ক্রেতারা তার ওয়েবসাইটে বেশি সময় থাকে।",
          featuredPanelBody:
            "এই অংশটি এখন সাধারণ পণ্যের ব্লকের চেয়ে বেশি একটি পেশাদার B2B ক্রয়-পরিবেশের মতো, যেখানে আস্থা, কাঠামো এবং পরিষ্কার পরবর্তী অগ্রগতি আছে।",
          featuredPrimaryCta: "একটি জিজ্ঞাসা শুরু করুন",
          featuredSecondaryCta: "কমপ্লায়েন্স দেখুন",
          realCatalogMode: "রিয়েল ক্যাটালগ মোড",
          realCatalogTitle: "এই ওয়েবসাইট এখন কেবল বাস্তব লাইভ পণ্যের তথ্য দেখায়, নমুনা ইনভেন্টরি নয়।",
          realCatalogBody:
            "এতে ক্রেতারা ডেমো স্টোরফ্রন্টের বদলে আরও সৎ সোর্সিং অভিজ্ঞতা পায়। নির্বাচিত পণ্য এখনও প্রকাশের অপেক্ষায় থাকলেও পেজটি পেশাদার B2B গল্পই তুলে ধরে।",
          nextActionLabel: "সেরা পরবর্তী পদক্ষেপ",
          nextActionTitle: "ট্রেড ডেস্ককে আপনার প্রয়োজন সরাসরি জানান।",
          nextActionBody:
            "যাদের নির্দিষ্ট পণ্য, গন্তব্য বাজার, MOQ বা ডকুমেন্টেশন দরকার, তারা এখনই সরাসরি সোর্সিং আলোচনায় গেলে দ্রুত এগোতে পারবেন।",
          nextActionPrimaryCta: "সোর্সিং অনুরোধ পাঠান",
          nextActionSecondaryCta: "কোম্পানির বিস্তারিত দেখুন",
          whySectionMatters: "এই অংশটি কেন গুরুত্বপূর্ণ",
          whySectionMattersBody:
            "একজন সিরিয়াস ক্রেতা ওয়েবসাইটে যত বেশি প্রমাণ, প্রক্রিয়া ও পরবর্তী পদক্ষেপ দেখতে পায়, কোম্পানিটি তত বেশি বিশ্বাসযোগ্য মনে হয়।",
          complianceBadge: "কমপ্লায়েন্স ও সার্টিফিকেশন ব্যানার",
          complianceTitle: "মেডিকেল ও ইন্ডাস্ট্রিয়াল ট্রেড—দুই ক্ষেত্রেই দৃশ্যমান কমপ্লায়েন্স ভাষা দরকার, তবে তা দায়িত্বশীলভাবে উপস্থাপন করতে হবে।",
          complianceBody:
            "এই অনুভূমিক ব্যানারটি সেই মান ও বাজার-প্রবেশ রেফারেন্সগুলো দেখায় যা ক্রেতারা প্রত্যাশা করে: মেডিকেল-ডিভাইস কোয়ালিটি সিস্টেম, ইউরোপীয় কনফর্মিটি, FDA-সংক্রান্ত ডকুমেন্টেশন সচেতনতা এবং বাস্তব চেম্বার সদস্যপদ।",
          complianceNote:
            "বাস্তব সার্টিফিকেশন, রেজিস্ট্রেশন ও ডকুমেন্টেশনের পরিধি পণ্যের ধরন, সরবরাহকারী, গন্তব্য ও লেনদেন কাঠামোর ওপর নির্ভর করে। এই ব্যানারটি প্রত্যেক পণ্যের জন্য সার্বজনীন সার্টিফিকেশন দাবি নয়; বরং অপারেশনাল পরিচিতি ও বাস্তব উন্মুক্ত যোগ্যতা তুলে ধরে।",
          portalBadge: "ক্লায়েন্ট সাপ্লাই চেইন পোর্টাল",
          portalTitle: "বর্তমান শিপমেন্ট প্রযুক্তিকে আরও পেশাদার B2B ক্লায়েন্ট সাপ্লাই-চেইন টুল হিসেবে উপস্থাপন করুন।",
          portalBody:
            "যাচাইকৃত আমদানিকারকরা নির্দিষ্ট পরিচয়পত্র ব্যবহার করে একটি আরও পরিপাটি ক্লায়েন্ট পোর্টালে ঢুকতে পারে, যেখানে বাল্ক শিপমেন্ট ট্র্যাকিং, ইনভয়েস সমন্বয় ও নিরাপদ পুনরায় অর্ডার প্রক্রিয়া আছে।",
          portalMetric1: "যাচাইকৃত ক্লায়েন্ট প্রবেশাধিকার",
          portalMetric1Body: "Access ID ও Access Code-এর মাধ্যমে গ্রাহক-স্তরের অ্যাক্সেস নিয়ন্ত্রণ",
          portalMetric2: "বাল্ক শিপমেন্ট ট্র্যাকিং",
          portalMetric2Body: "রেফারেন্স, চেকপয়েন্ট ও ETA উইন্ডোর ওপর ভিত্তি করে পেশাদার ট্র্যাকিং",
          portalMetric3: "রি-অর্ডার ও ইনভয়েস সমন্বয়",
          portalMetric3Body: "পুনরাবৃত্ত সরবরাহ ও ডকুমেন্ট ফলো-আপের জন্য আরও গঠিত পথ",
          portalFormTitle: "যাচাইকৃত আমদানিকারক সাইন-ইন",
          portalFormBody:
            "অ্যাডমিন প্যানেল থেকে তৈরি করা গ্রাহকের Access ID ও Access Code ব্যবহার করে এই অংশ থেকেই আধুনিক ক্লায়েন্ট সাপ্লাই-চেইন পোর্টালে সাইন ইন করুন।",
          portalIdLabel: "Access ID",
          portalCodeLabel: "Access Code",
          portalOpenCta: "পোর্টালে সাইন ইন করুন",
          portalTalkCta: "ট্রেড ডেস্কের সাথে কথা বলুন",
          portalSigningIn: "সাইন ইন হচ্ছে...",
          portalMissingFields: "Access ID এবং Access Code দুটিই লিখুন।",
          portalLoginSuccess: "ক্লায়েন্ট পোর্টালে সফলভাবে লগইন হয়েছে।",
          portalSignedInLabel: "পোর্টাল অ্যাক্সেস সক্রিয়",
          portalSignedInTitle: "এই অংশ থেকেই এখন ক্লায়েন্ট সাপ্লাই-চেইন পোর্টাল খোলা আছে।",
          portalSignedInBody:
            "আপনি গ্রাহকভিত্তিক অ্যাক্সেসে সাইন ইন করেছেন, তাই নিরাপদ ওয়ার্কস্পেসে এখন শুধু ওই ক্রেতার অর্ডার, চালান ও ফলো-আপ ডেটাই দৃশ্যমান থাকবে।",
          portalCustomerLabel: "গ্রাহক",
          portalOrdersLabel: "অর্ডার",
          portalShipmentsLabel: "চালান",
          portalOrderVisibility: "পোর্টালে কেবল এই ক্রেতার নিজস্ব অর্ডারগুলোই দৃশ্যমান।",
          portalShipmentVisibility: "পোর্টালে কেবল এই ক্রেতার নিজস্ব চালানগুলোই দৃশ্যমান।",
          portalRecentOrders: "সাম্প্রতিক অর্ডার",
          portalRecentShipments: "সাম্প্রতিক চালান",
          portalNoOrders: "এখনও কোনো সংযুক্ত গ্রাহক অর্ডার নেই।",
          portalNoShipments: "এখনও কোনো সংযুক্ত গ্রাহক চালান নেই।",
          portalOpenWorkspace: "ক্লায়েন্ট ওয়ার্কস্পেস খুলুন",
          portalTrackLink: "শিপমেন্ট ট্র্যাকিং দেখুন",
          portalSignOut: "সাইন আউট",
          portalCard1: "বাল্ক শিপমেন্ট ট্র্যাক করুন",
          portalCard1Body: "মাইলস্টোন আপডেট, বন্দর চেকপয়েন্ট ও ETA উইন্ডো পর্যালোচনা করুন।",
          portalCard2: "ইনভয়েস সমন্বয় পরিচালনা করুন",
          portalCard2Body: "রেফারেন্স, ডকুমেন্ট ও পেমেন্ট-ধাপের যোগাযোগ সুসংগঠিত রাখুন।",
          portalCard3: "নিরাপদে পুনরায় অর্ডার করুন",
          portalCard3Body: "যাচাইকৃত ক্রেতারা অনেক দ্রুত রিপিট-অর্ডার ওয়ার্কফ্লোতে ফিরে যেতে পারেন।",
          globalCard1Label: "সদরদপ্তর ও নিয়ন্ত্রণ",
          globalCard1Title: "বাংলাদেশ",
          globalCard1Body:
            "ক্রেতা যোগাযোগ, ট্রেড ডেস্ক সমন্বয় এবং সম্পর্ক ব্যবস্থাপনা আপনার বাংলাদেশ-ভিত্তিক সদরদপ্তরকে কেন্দ্র করেই পরিচালিত হয়।",
          globalCard2Label: "বাণিজ্য করিডর",
          globalCard2Title: "চীন · জর্জিয়া · ইউরোপ",
          globalCard2Body:
            "ওয়েবসাইটটি এখন আপনার সোর্সিং ও বাজারমুখী সম্পর্কগুলোকে অস্পষ্ট ভৌগোলিক দাবি নয়, দৃশ্যমান আন্তর্জাতিক পথ হিসেবে উপস্থাপন করে।",
          operationsHubLabel: "অপারেশনস হাব",
          operationsHubTitle: "বাংলাদেশ সদরদপ্তর",
          governancePreviewLabel: "ডিউ-ডিলিজেন্স প্রিভিউ",
          governancePreviewTitle: "ক্রেতা প্রথম চুক্তির খসড়া পাঠানোর আগেই কর্পোরেট কাঠামো দৃশ্যমান মনে হওয়া উচিত।",
          governancePreviewBody:
            "এই গভর্ন্যান্স প্রিভিউটি এমন দ্বিধা কমাতে তৈরি করা হয়েছে যা প্রায়ই গুরুতর B2B আলোচনায় প্রশ্ন তোলে—‘এই কোম্পানির পেছনে কারা আছে?’",
          portalFallbackCustomer: "গ্রাহক",
          portalFallbackAccess: "অ্যাক্সেস জারি হয়েছে",
          portalFallbackShipmentOpen: "চালান প্রক্রিয়াধীন",
        }
      : {
        heroBadge: "Trusted by importers in 30+ countries",
        heroHeadline: settings?.hero_headline || "Connecting Global Markets, Delivering Trust.",
        heroSubheadline:
          settings?.hero_subheadline ||
          "R.S. International — Importer, seller and marketer of all types of products. General order collector & supplier serving B2B partners worldwide.",
        heroPrimaryCta: settings?.hero_cta_label || "Inquire Now",
        heroSecondaryCta: "Browse Catalog",
        heroTrust1: "ISO and trade-compliance aligned",
        heroTrust2: "Global sourcing coordination network",
        heroTrust3: "Door-to-door logistics support",
        divisionsBadge: "Dedicated Product Divisions",
        divisionsTitle: "Right below the hero, buyers should immediately understand exactly what your company trades.",
        divisionsBody:
          "Large B2B buyers want to see clear sector specialization, technical familiarity, and bulk-order readiness before they continue. These division cards lead directly into specification-style details and bulk ordering pathways.",
        divisionPrimaryCta: "View B2B specs",
        divisionSecondaryCta: "Bulk ordering path",
        divisionDetailCta: "Request full spec pack",
        trustBadge: "Product & Trust Architecture",
        trustPanelTitle: "Importers trust companies that can prove structure, discipline, and commercial readiness.",
        trustPanelBody:
          "This section now does more than list categories. It shows the signals that make cautious buyers continue evaluating your company instead of bouncing after the first glance.",
        trustCenterCta: "Review trust center",
        companyStoryCta: "Read company profile",
        proofEstablished: "Established",
        proofCompliance: "Compliance visibility",
        proofResponse: "Trade response",
        proofBase: "Company base",
        proofEstablishedValue: settings?.company_since ? `Since ${settings.company_since}` : "Long-term trade legacy",
        proofComplianceValue: `${complianceCount} public trust documents`,
        proofResponseValue: "Quote replies within 24 business hours",
        proofBaseValue: settings?.company_location || settings?.address || "Barishal, Bangladesh",
        confidenceTitle1: "Verification before recommendation",
        confidenceBody1: "Supplier documents, execution fit, and quality practicality are reviewed before a sourcing discussion becomes a real order path.",
        confidenceTitle2: "One accountable trade desk",
        confidenceBody2: "Buyers deal with a clear business contact point instead of being pushed through fragmented intermediaries and uncertain channels.",
        confidenceTitle3: "Follow-through across delivery",
        confidenceBody3: "From quotation and negotiation to shipment coordination and buyer communication, the process stays organized and traceable.",
        seriousImporterMessage:
          "When buyers see clear company identity, visible compliance, and disciplined process signals, they stay longer and trust the business behind the website more quickly.",
        fullCatalogCta: "View full catalog",
        globalBadge: "Global Reach & Operations",
        globalTitle: "International trust grows faster when buyers can see how you actually operate across borders.",
        globalBody:
          "This infographic-style section highlights your headquarters in Bangladesh and visually frames trade corridors and partnerships across China, Georgia, and the wider European market.",
        governanceBadge: "Corporate Governance Preview",
        governanceTitle: "Before signing larger contracts, B2B buyers want to know who is behind the corporation.",
        governanceBody:
          "A visible mission statement, founding history, and leadership path make the company feel more serious, more governable, and more suitable for high-value commercial relationships.",
        governanceMissionLabel: "Mission statement",
        governanceMission:
          "Build trusted cross-border buying relationships through disciplined communication, structured execution, and commercially responsible sourcing support.",
        governanceHistoryLabel: "Founding history",
        governanceHistory: settings?.company_since
          ? `R.S. International has been building its trade identity since ${settings.company_since}, with continuity carried forward through the company's current leadership structure.`
          : "R.S. International has a long-running trade foundation carried forward through the company's current leadership structure.",
        governanceLeadershipLabel: "Leadership & corporate profile",
        governanceLeadership:
          "Prospects can continue into the corporate profile, founder legacy, and leadership pages to build a more complete due-diligence impression before initiating a large transaction.",
        governancePrimaryCta: "Open corporate profile",
        governanceSecondaryCta: "Meet leadership",
        featuredBadge: "Featured Buyer Journey",
        featuredTitle: "The websites that hold serious buyers longer are the ones that help them evaluate risk, process, and next steps.",
        featuredSubtitle:
          "When a prospect can review sourcing logic, company credibility, and the right next action in one place, the website feels more reliable and more appropriate for real B2B procurement.",
        retentionTitle1: "Clear cross-border communication",
        retentionBody1: "The more precisely product, delivery, and documentation questions are answered, the more likely overseas buyers are to keep exploring.",
        retentionCta1: "See company profile",
        retentionTitle2: "Trust content buyers can review",
        retentionBody2: "Certificates, company history, and public compliance materials give cautious buyers stronger reasons to keep evaluating your business.",
        retentionCta2: "Open trust center",
        retentionTitle3: "Fast next steps after interest",
        retentionBody3: "Visible response standards and clean business pathways reduce hesitation and make it easier for serious buyers to move forward.",
        retentionCta3: "Explore support flow",
        featuredPanelLabel: "Buyer retention layer",
        featuredPanelTitle: "Serious buyers stay longer on websites that prove how the business works.",
        featuredPanelBody:
          "This section is designed to feel less like a generic product block and more like a professional B2B buying environment with trust, structure, and clear forward motion.",
        featuredPrimaryCta: "Start an inquiry",
        featuredSecondaryCta: "Review compliance",
        realCatalogMode: "Real catalog mode",
        realCatalogTitle: "This website now shows only real live product data, not sample inventory.",
        realCatalogBody:
          "That gives buyers a more honest sourcing impression than a demo storefront. Even when featured products are still being published, the page keeps presenting a credible B2B story.",
        nextActionLabel: "Best next action",
        nextActionTitle: "Tell the trade desk what you need.",
        nextActionBody:
          "Buyers with a product target, destination market, MOQ expectation, or documentation need can move faster by opening a direct sourcing conversation now.",
        nextActionPrimaryCta: "Submit sourcing request",
        nextActionSecondaryCta: "Review company details",
        whySectionMatters: "Why this section matters",
        whySectionMattersBody:
          "The longer a serious buyer can review proof, process, and next steps on the website, the more trustworthy the company appears.",
        complianceBadge: "Compliance & Certification Banner",
        complianceTitle: "Medical and industrial trade both demand visible compliance language, but it must be presented responsibly.",
        complianceBody:
          "This horizontal banner frames the standards and market-access references buyers expect to see: medical-device quality systems, European conformity language, FDA-facing documentation awareness, and real chamber membership.",
        complianceNote:
          "Actual certification, registration, and documentary scope depend on product type, supplier, destination, and transaction structure. This banner communicates operational familiarity plus real public credentials, not blanket certification claims for every item.",
        portalBadge: "Client Supply Chain Portal",
        portalTitle: "Reframe the existing shipment technology as a more professional B2B client supply-chain tool.",
        portalBody:
          "Verified importers can use dedicated credentials to access a cleaner client portal for bulk-shipment tracking, invoice coordination, and secure reorder workflows.",
        portalMetric1: "Verified client access",
        portalMetric1Body: "Customer-level access control using Access ID and Access Code",
        portalMetric2: "Bulk shipment tracking",
        portalMetric2Body: "Professional tracking around references, checkpoints, and ETA windows",
        portalMetric3: "Reorder & invoice coordination",
        portalMetric3Body: "A more structured path for repeat supply and document follow-up",
        portalFormTitle: "Verified importer sign-in",
        portalFormBody:
          "Enter the customer Access ID and Access Code generated from the admin panel to sign in directly to the modern client supply chain portal from this section.",
        portalIdLabel: "Access ID",
        portalCodeLabel: "Access Code",
        portalOpenCta: "Sign in to portal",
        portalTalkCta: "Talk to trade desk",
        portalSigningIn: "Signing in...",
        portalMissingFields: "Enter both the Access ID and Access Code.",
        portalLoginSuccess: "Client portal login successful.",
        portalSignedInLabel: "Portal access active",
        portalSignedInTitle: "The client supply chain portal is now open from this section.",
        portalSignedInBody:
          "You are signed in with customer-specific access, so only that buyer's orders, shipments, and follow-up data remain visible inside the secure workspace.",
        portalCustomerLabel: "Customer",
        portalOrdersLabel: "Orders",
        portalShipmentsLabel: "Shipments",
        portalOrderVisibility: "Only this buyer's own orders are visible in the portal.",
        portalShipmentVisibility: "Only this buyer's own shipments are visible in the portal.",
        portalRecentOrders: "Recent orders",
        portalRecentShipments: "Recent shipments",
        portalNoOrders: "No linked customer orders yet.",
        portalNoShipments: "No linked customer shipments yet.",
        portalOpenWorkspace: "Open client workspace",
        portalTrackLink: "Review shipment tracking",
        portalSignOut: "Sign out",
        portalCard1: "Track bulk shipments",
        portalCard1Body: "Review milestone updates, port checkpoints, and ETA windows.",
        portalCard2: "Manage invoice coordination",
        portalCard2Body: "Keep references, documents, and payment-stage communication organized.",
        portalCard3: "Reorder supply lots securely",
        portalCard3Body: "Verified buyers can return to repeat-order workflows much faster.",
        globalCard1Label: "HQ & control",
        globalCard1Title: "Bangladesh",
        globalCard1Body:
          "Buyer communication, trade desk coordination, and relationship management remain anchored in your Bangladesh base.",
        globalCard2Label: "Trade corridors",
        globalCard2Title: "China · Georgia · Europe",
        globalCard2Body:
          "The site now frames your sourcing and market-facing relationships as visible international pathways instead of generic geography claims.",
        operationsHubLabel: "Operations hub",
        operationsHubTitle: "Bangladesh HQ",
        governancePreviewLabel: "Due-diligence preview",
        governancePreviewTitle:
          "Corporate structure should feel visible before the buyer ever sends the first contract draft.",
        governancePreviewBody:
          "This governance preview is designed to reduce the “who is behind this company?” hesitation that often slows serious B2B conversations.",
        portalFallbackCustomer: "Customer",
        portalFallbackAccess: "Access issued",
        portalFallbackShipmentOpen: "Shipment open",
      };

  const operationsNodes = isZh
    ? [
        { title: "孟加拉国总部", body: "总部与客户协调中心", meta: settings?.company_location || "Barishal, Bangladesh" },
        { title: "中国通道", body: "制造与供应协作节点", meta: "工厂沟通与采购协同" },
        { title: "格鲁吉亚联结", body: "区域通道与商业拓展桥梁", meta: "区域市场与中介贸易定位" },
        { title: "欧洲市场通道", body: "面向欧洲市场的准入与交付沟通", meta: "注重合规的买家协调" },
      ]
    : isBn
      ? [
          { title: "বাংলাদেশ সদরদপ্তর", body: "সদরদপ্তর ও ক্রেতা সমন্বয় কেন্দ্র", meta: settings?.company_location || "Barishal, Bangladesh" },
          { title: "চীন করিডর", body: "উৎপাদন ও সরবরাহপক্ষ সমন্বয়", meta: "কারখানা যোগাযোগ ও সোর্সিং সমন্বয়" },
          { title: "জর্জিয়া সংযোগ", body: "আঞ্চলিক বাণিজ্যিক সেতু", meta: "বাজারে প্রবেশ ও মধ্যবর্তী বাণিজ্য অবস্থান" },
          { title: "ইউরোপ অ্যাক্সেস", body: "ইউরোপীয় বাজারমুখী ডেলিভারি সমন্বয়", meta: "কমপ্লায়েন্স-সংবেদনশীল ক্রেতা সহায়তা" },
        ]
      : [
        { title: "Bangladesh HQ", body: "Headquarters and buyer coordination base", meta: settings?.company_location || "Barishal, Bangladesh" },
        { title: "China Corridor", body: "Manufacturing and supply-side coordination", meta: "Factory communication and sourcing alignment" },
        { title: "Georgia Link", body: "Regional bridge for commercial movement", meta: "Intermediary trade and market access positioning" },
        { title: "Europe Access", body: "European-market facing delivery dialogue", meta: "Compliance-sensitive buyer coordination" },
      ];

  const complianceBadges = isZh
    ? [
        { code: "ISO 13485", title: "医疗器械质量体系语言", note: "面向医疗买家的质量与文件对话框架" },
        { code: "CE", title: "欧洲市场合规语言", note: "围绕欧盟准入与文件要求进行更专业沟通" },
        { code: "FDA", title: "美国市场文件意识", note: "针对 FDA 相关文件与注册需求保持业务理解" },
        { code: "BCCI", title: "商会成员与真实资质", note: "结合真实公开证书与商会成员身份建立信任" },
      ]
    : isBn
      ? [
          { code: "ISO 13485", title: "মেডিকেল-ডিভাইস কোয়ালিটি ভাষা", note: "গুরুত্বপূর্ণ স্বাস্থ্যসেবা ক্রেতাদের আলোচনার প্রস্তুতির সংকেত" },
          { code: "CE", title: "ইউরোপীয় কনফর্মিটি পথ", note: "ইইউমুখী বাণিজ্যের জন্য পণ্য ও ডকুমেন্ট প্রত্যাশা নির্ধারণ করে" },
          { code: "FDA", title: "যুক্তরাষ্ট্রের ডকুমেন্টেশন সচেতনতা", note: "FDA-সংবেদনশীল ক্যাটাগরিতে আরও বিশ্বাসযোগ্য আলোচনাকে সমর্থন করে" },
          { code: "BCCI", title: "চেম্বার সদস্যপদের বিশ্বাসযোগ্যতা", note: "বাস্তব উন্মুক্ত সনদ ও ট্রেড-পরিচয় প্রমাণ দ্বারা সমর্থিত" },
        ]
      : [
        { code: "ISO 13485", title: "Medical-device quality language", note: "Signals readiness for serious healthcare-buyer conversations" },
        { code: "CE", title: "European conformity pathway", note: "Frames product and document expectations for EU-facing trade" },
        { code: "FDA", title: "US-market documentation awareness", note: "Supports more credible discussion for FDA-sensitive categories" },
        { code: "BCCI", title: "Chamber membership credibility", note: "Backed by real public certificates and trade-identity proof" },
      ];

  const openPortal = async (event) => {
    event.preventDefault();
    setPortalError("");
    const nextAccessId = portalAccessId.trim().toUpperCase();
    const nextAccessCode = portalAccessCode.trim();
    if (!nextAccessId || !nextAccessCode) {
      setPortalError(copy.portalMissingFields);
      return;
    }
    const result = await customerSession.login(nextAccessId, nextAccessCode);
    if (!result.ok) {
      setPortalError(result.error);
      return;
    }
    setPortalAccessCode("");
    toast.success(copy.portalLoginSuccess);
    openWorkspace();
  };

  const openWorkspace = () => {
    navigate("/client-portal");
  };

  return (
    <>
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden -mt-20" data-testid="hero-section">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          src={heroVideo}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A1341]/85 via-[#2A1341]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#101D38]/70 to-transparent" />

        <div className="relative h-full container-rsi flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl text-white pt-20"
          >
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 backdrop-blur-md"
              data-testid="hero-badge"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#bfa3ff]" />
              {copy.heroBadge}
            </span>
            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mt-6"
              data-testid="hero-headline"
            >
              {copy.heroHeadline}
            </h1>
            <p
              className="mt-6 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {copy.heroSubheadline}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                onClick={() => setRfqOpen(true)}
                className="btn-primary !bg-white !text-[var(--rsi-purple)] hover:!bg-white/90"
                data-testid="hero-cta-inquire"
              >
                {copy.heroPrimaryCta}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-8 text-white/75">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck className="h-5 w-5 text-[#9be0b3]" />
                {copy.heroTrust1}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Globe2 className="h-5 w-5 text-[#9be0b3]" />
                {copy.heroTrust2}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Truck className="h-5 w-5 text-[#9be0b3]" />
                {copy.heroTrust3}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="global-operations-section">
        <div className="container-rsi">
          <div className="grid gap-8 xl:grid-cols-[minmax(0,0.95fr)_minmax(420px,1.05fr)] xl:items-center">
            <div>
              <span className="badge-purple">
                <Globe2 className="h-3.5 w-3.5" /> {copy.globalBadge}
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
                {copy.globalTitle}
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--rsi-text-soft)] sm:text-lg">
                {copy.globalBody}
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="surface-card p-5">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{copy.globalCard1Label}</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    {copy.globalCard1Title}
                  </div>
                  <p className="mt-2 text-sm leading-7 text-[var(--rsi-text-soft)]">
                    {copy.globalCard1Body}
                  </p>
                </div>
                <div className="surface-card p-5">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{copy.globalCard2Label}</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    {copy.globalCard2Title}
                  </div>
                  <p className="mt-2 text-sm leading-7 text-[var(--rsi-text-soft)]">
                    {copy.globalCard2Body}
                  </p>
                </div>
              </div>
            </div>

            <div className="home-ops-map">
              <div className="home-ops-map__core">
                <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/66">{copy.operationsHubLabel}</div>
                <div className="mt-2 font-display text-3xl font-black tracking-tight text-white">{copy.operationsHubTitle}</div>
                <div className="mt-2 text-sm leading-7 text-white/76">
                  {settings?.address || "Natun Bazar, Barishal Sadar, Barishal, Bangladesh"}
                </div>
              </div>
              <div className="home-ops-map__grid">
                {operationsNodes.map((node) => (
                  <div key={node.title} className="home-ops-node">
                    <div className="home-ops-node__dot" />
                    <div className="home-ops-node__title">{node.title}</div>
                    <div className="home-ops-node__body">{node.body}</div>
                    <div className="home-ops-node__meta">{node.meta}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-[var(--rsi-bg-subtle)]" data-testid="compliance-banner-section">
        <div className="container-rsi">
          <div className="mb-6 max-w-3xl">
            <span className="badge-purple">
              <FileText className="h-3.5 w-3.5" /> {copy.complianceBadge}
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-[var(--rsi-navy)]">
              {copy.complianceTitle}
            </h2>
            <p className="mt-4 text-sm sm:text-base leading-7 text-[var(--rsi-text-soft)]">
              {copy.complianceBody}
            </p>
          </div>

          <div className="home-compliance-strip">
            {complianceBadges.map((item) => (
              <div key={item.code} className="home-compliance-pill">
                <div className="home-compliance-pill__code">{item.code}</div>
                <div className="home-compliance-pill__title">{item.title}</div>
                <div className="home-compliance-pill__note">{item.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-[24px] border border-[rgba(42,19,65,0.08)] bg-white/90 px-5 py-4 text-sm leading-7 text-[var(--rsi-text-soft)] shadow-[0_18px_50px_-36px_rgba(42,19,65,0.24)]">
            {copy.complianceNote}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="governance-preview-section">
        <div className="container-rsi">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.9fr)] lg:items-start">
            <div>
              <span className="badge-purple">
                <Building2 className="h-3.5 w-3.5" /> {copy.governanceBadge}
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
                {copy.governanceTitle}
              </h2>
              <p className="mt-5 max-w-3xl text-base leading-7 text-[var(--rsi-text-soft)] sm:text-lg">
                {copy.governanceBody}
              </p>
            </div>
            <div className="surface-card home-governance-card p-6 sm:p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--rsi-purple)]">
                {copy.governancePreviewLabel}
              </div>
              <div className="mt-4 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                {copy.governancePreviewTitle}
              </div>
              <p className="mt-3 text-sm leading-7 text-[var(--rsi-text-soft)]">
                {copy.governancePreviewBody}
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="surface-card p-6 sm:p-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{copy.governanceMissionLabel}</div>
              <div className="mt-3 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.company_name || "R.S. INTERNATIONAL"}
              </div>
              <p className="mt-4 text-sm leading-7 text-[var(--rsi-text-soft)]">{copy.governanceMission}</p>
            </div>
            <div className="surface-card p-6 sm:p-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{copy.governanceHistoryLabel}</div>
              <div className="mt-3 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.company_since ? `${settings.company_since}` : "Founding legacy"}
              </div>
              <p className="mt-4 text-sm leading-7 text-[var(--rsi-text-soft)]">{copy.governanceHistory}</p>
            </div>
            <div className="surface-card p-6 sm:p-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{copy.governanceLeadershipLabel}</div>
              <div className="mt-3 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.proprietor || "Leadership"}
              </div>
              <p className="mt-4 text-sm leading-7 text-[var(--rsi-text-soft)]">{copy.governanceLeadership}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/about" className="btn-primary" data-testid="governance-about-link">
                  {copy.governancePrimaryCta} <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/ceo-profile" className="btn-ghost" data-testid="governance-ceo-link">
                  {copy.governanceSecondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden" data-testid="cta-strip">
        <div className="absolute inset-0 home-portal-bg grain" />
        <div className="container-rsi relative grid gap-10 xl:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] items-start text-white">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white/84">
              <ScanSearch className="h-3.5 w-3.5" /> {copy.portalBadge}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tighter">
              {copy.portalTitle}
            </h2>
            <p className="mt-4 max-w-2xl text-white/82 text-base leading-7">
              {copy.portalBody}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">{copy.portalMetric1}</div>
                <div className="mt-2 text-lg font-bold text-white">{copy.portalMetric1Body}</div>
              </div>
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">{copy.portalMetric2}</div>
                <div className="mt-2 text-lg font-bold text-white">{copy.portalMetric2Body}</div>
              </div>
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">{copy.portalMetric3}</div>
                <div className="mt-2 text-lg font-bold text-white">{copy.portalMetric3Body}</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/track" className="btn-outline-light" data-testid="strip-cta-track">
                <Truck className="h-4 w-4" /> {copy.portalCard1}
              </Link>
              <Link to="/app" className="btn-outline-light" data-testid="strip-cta-app">
                <ScanSearch className="h-4 w-4" /> {copy.portalOpenCta}
              </Link>
            </div>
          </div>

          <div className="home-portal-shell">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">
              {copy.portalFormTitle}
            </div>
            <div className="mt-3 font-display text-2xl font-black tracking-tight text-white">
              {copy.portalBadge}
            </div>
            <p className="mt-3 text-sm leading-7 text-white/78">
              {copy.portalFormBody}
            </p>

            {customerSession.isAuthenticated ? (
              <div className="mt-6 space-y-4">
                <div className="rounded-[24px] border border-white/12 bg-white/10 px-5 py-5">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/64">
                    {copy.portalSignedInLabel}
                  </div>
                  <div className="mt-3 font-display text-2xl font-black tracking-tight text-white">
                    {copy.portalSignedInTitle}
                  </div>
                  <p className="mt-3 text-sm leading-7 text-white/78">
                    {copy.portalSignedInBody}
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="home-portal-mini-card">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/58">
                      {copy.portalCustomerLabel}
                    </div>
                    <div className="mt-3 text-sm font-semibold text-white">
                      {customerSession.session?.customer?.customer_name || copy.portalFallbackCustomer}
                    </div>
                    <div className="mt-2 text-xs leading-6 text-white/72">
                      {customerSession.session?.customer?.customer_access_id || copy.portalFallbackAccess}
                    </div>
                  </div>
                  <div className="home-portal-mini-card">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/58">
                      {copy.portalOrdersLabel}
                    </div>
                    <div className="mt-3 text-sm font-semibold text-white">
                      {(customerSession.session?.orders || []).length}
                    </div>
                    <div className="mt-2 text-xs leading-6 text-white/72">
                      {copy.portalOrderVisibility}
                    </div>
                  </div>
                  <div className="home-portal-mini-card">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/58">
                      {copy.portalShipmentsLabel}
                    </div>
                    <div className="mt-3 text-sm font-semibold text-white">
                      {(customerSession.session?.shipments || []).length}
                    </div>
                    <div className="mt-2 text-xs leading-6 text-white/72">
                      {copy.portalShipmentVisibility}
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="home-portal-mini-card">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/58">
                      {copy.portalRecentOrders}
                    </div>
                    <div className="mt-3 space-y-2">
                      {(customerSession.session?.orders || []).length ? (
                        customerSession.session.orders.slice(0, 2).map((item) => (
                          <div key={item.id} className="rounded-2xl bg-white/8 px-3 py-3">
                            <div className="text-sm font-semibold text-white">{item.order_number}</div>
                            <div className="mt-1 text-xs text-white/68 capitalize">
                              {String(item.status || "").replaceAll("_", " ")}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs leading-6 text-white/72">{copy.portalNoOrders}</div>
                      )}
                    </div>
                  </div>
                  <div className="home-portal-mini-card">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/58">
                      {copy.portalRecentShipments}
                    </div>
                    <div className="mt-3 space-y-2">
                      {(customerSession.session?.shipments || []).length ? (
                        customerSession.session.shipments.slice(0, 2).map((item) => (
                          <div key={item.id} className="rounded-2xl bg-white/8 px-3 py-3">
                            <div className="text-sm font-semibold text-white">{item.tracking_id || "Shipment"}</div>
                            <div className="mt-1 text-xs text-white/68">
                              {item.current_location || item.destination || copy.portalFallbackShipmentOpen}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs leading-6 text-white/72">{copy.portalNoShipments}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={openWorkspace}
                    className="btn-primary !bg-white !text-[var(--rsi-purple)] hover:!bg-white/90"
                    data-testid="portal-open-submit"
                  >
                    {copy.portalOpenWorkspace} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <Link to="/track" className="btn-outline-light">
                    {copy.portalTrackLink}
                  </Link>
                  <button type="button" onClick={customerSession.logout} className="btn-outline-light">
                    {copy.portalSignOut}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={openPortal} className="mt-6 grid gap-4">
                <div className="grid gap-2">
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-white/64">
                    {copy.portalIdLabel}
                  </label>
                  <input
                    value={portalAccessId}
                    onChange={(event) => setPortalAccessId(event.target.value.toUpperCase())}
                    className="home-portal-input"
                    placeholder={copy.portalIdLabel}
                    data-testid="portal-access-id"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-white/64">
                    {copy.portalCodeLabel}
                  </label>
                  <input
                    type="password"
                    value={portalAccessCode}
                    onChange={(event) => setPortalAccessCode(event.target.value)}
                    className="home-portal-input"
                    placeholder={copy.portalCodeLabel}
                    data-testid="portal-access-code"
                  />
                </div>
                {portalError ? (
                  <div className="rounded-2xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                    {portalError}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    className="btn-primary !bg-white !text-[var(--rsi-purple)] hover:!bg-white/90"
                    data-testid="portal-open-submit"
                    disabled={customerSession.loading}
                  >
                    {customerSession.loading ? copy.portalSigningIn : copy.portalOpenCta} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <Link to="/contact" className="btn-outline-light">
                    {copy.portalTalkCta}
                  </Link>
                </div>
              </form>
            )}

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="home-portal-mini-card">
                <Truck className="h-4 w-4 text-white" />
                <div className="mt-3 text-sm font-semibold text-white">{copy.portalCard1}</div>
                <div className="mt-2 text-xs leading-6 text-white/72">{copy.portalCard1Body}</div>
              </div>
              <div className="home-portal-mini-card">
                <ClipboardList className="h-4 w-4 text-white" />
                <div className="mt-3 text-sm font-semibold text-white">{copy.portalCard2}</div>
                <div className="mt-2 text-xs leading-6 text-white/72">{copy.portalCard2Body}</div>
              </div>
              <div className="home-portal-mini-card">
                <Factory className="h-4 w-4 text-white" />
                <div className="mt-3 text-sm font-semibold text-white">{copy.portalCard3}</div>
                <div className="mt-2 text-xs leading-6 text-white/72">{copy.portalCard3Body}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RFQDialog open={rfqOpen} onOpenChange={setRfqOpen} />
    </>
  );
}
